/**
 * Tome vibes (lane N, 2026-10-07): named, typed configs that give a window its
 * nature by slight modulation, not by new CSS. A vibe bundles
 *
 *   shape    edge, corners, roughness, bow     (outline; tomeShape.ts)
 *   texture  paper kind, grain, stains, burn   (TomeSkin, tome-shape.css)
 *   frame    a frame preset or placements      (TomeFrame.tsx)
 *   palette  parchment, cover, gilt, accent, burn inks
 *
 * `<TomePanel vibe="moonfall">` applies one, and explicit props still win.
 * The palette and amounts are published as CSS custom properties on the
 * panel root (`tomeVibeStyle`), so any window, tome or not, can adopt the same
 * tokens by spreading that style. Adding a vibe is one `defineVibe` entry in
 * TOME_VIBES; the test suite rejects out-of-range values.
 */
import type React from 'react';
import type { TomeCorners, TomeEdge } from './tomeShape';
import type { TomeFramePiece, TomeFramePreset } from './TomeFrame';

export type TomeTexture = 'plain' | 'vellum' | 'aged';

/** Colours a vibe may set. Each maps to one `--tome-*` custom property. */
export interface TomeVibePalette {
  /** Parchment gradient: light top, middle, darker foot. */
  paperHi?: string; paperMid?: string; paperLo?: string;
  /** The woodland cover's gradient. */
  coverHi?: string; coverLo?: string;
  /** Gilt rules, flourishes and portrait frames. */
  gilt?: string;
  /** Eyebrows, ready chapters, sprigs. */
  accent?: string;
  /** Edge burn on the paper; scuff on the cover's edge. */
  burn?: string; scuff?: string;
}

export interface TomeVibeShape {
  edge: TomeEdge;
  corners: TomeCorners;
  /** Bite depth multiplier, 0..2. */
  roughness: number;
  /** Multiplier on each layer's slow edge bow, 0..2.5. */
  bow: number;
}

export interface TomeVibeTexture {
  kind: TomeTexture;
  /** Strength of grain and fibres, 0..1 (1 = the full tiles). */
  grain: number;
  /** Tide stains on the page (aged paper), 0..6. */
  stains: number;
  /** Edge burn strength, 0..2. */
  burn: number;
}

export interface TomeVibe {
  label: string;
  shape: TomeVibeShape;
  texture: TomeVibeTexture;
  frame: TomeFramePreset | TomeFramePiece[];
  palette: TomeVibePalette;
}

/** What a preset writes: everything but the label is optional and falls back to the default vibe. */
export interface TomeVibeInput {
  label: string;
  shape?: Partial<TomeVibeShape>;
  texture?: Partial<TomeVibeTexture>;
  frame?: TomeFramePreset | TomeFramePiece[];
  palette?: TomeVibePalette;
}

export const TOME_VIBE_RANGES = {
  roughness: [0, 2], bow: [0, 2.5], grain: [0, 1], stains: [0, 6], burn: [0, 2],
} as const satisfies Record<string, readonly [number, number]>;

const clamp = (value: number, [min, max]: readonly [number, number]) =>
  Number.isFinite(value) ? Math.min(max, Math.max(min, value)) : min;

const BASE: TomeVibe = {
  label: 'Tome',
  shape: { edge: 'deckled', corners: 'notched', roughness: 1, bow: 1 },
  texture: { kind: 'vellum', grain: 1, stains: 0, burn: 1 },
  frame: 'grove',
  palette: {},
};

/**
 * Build a vibe from a partial config over the default: numbers are clamped to
 * TOME_VIBE_RANGES (tests assert every preset is already in range, so a bad
 * value fails the suite), stains are whole, and the object literal's excess
 * keys are rejected by the type checker.
 */
export function defineVibe(input: TomeVibeInput, base: TomeVibe = BASE): TomeVibe {
  const shape = { ...base.shape, ...input.shape };
  const texture = { ...base.texture, ...input.texture };
  return {
    label: input.label,
    shape: { ...shape, roughness: clamp(shape.roughness, TOME_VIBE_RANGES.roughness), bow: clamp(shape.bow, TOME_VIBE_RANGES.bow) },
    texture: {
      ...texture,
      grain: clamp(texture.grain, TOME_VIBE_RANGES.grain),
      stains: Math.round(clamp(texture.stains, TOME_VIBE_RANGES.stains)),
      burn: clamp(texture.burn, TOME_VIBE_RANGES.burn),
    },
    frame: input.frame ?? base.frame,
    palette: { ...base.palette, ...input.palette },
  };
}

/** The named vibes. Keep them subtle variations of one tome. */
export const TOME_VIBES = {
  /** The tome as lane M drew it, with lane N's shape: warm vellum in a woodland cover. */
  default: defineVibe({ label: 'Tome' }),
  /** First Field and the woods: greener paper, a rougher bow, more of the grove on the frame. */
  grove: defineVibe({
    label: 'Grove',
    shape: { roughness: 1.15, bow: 1.3 },
    texture: { burn: 1.1 },
    palette: { paperHi: '#f6f3dc', paperMid: '#ebe5c5', paperLo: '#ddd4ae', coverHi: '#2f4a26', coverLo: '#101b0e', accent: '#2c5e23' },
  }),
  /** Moonfall Hollow: cool, silvered paper, cut corners, an indigo cover and moonlit rules. */
  moonfall: defineVibe({
    label: 'Moonfall',
    shape: { corners: 'chamfer', roughness: 0.85, bow: 0.8 },
    texture: { grain: 0.7, burn: 0.8 },
    frame: 'vines',
    palette: {
      paperHi: '#f3f2ec', paperMid: '#e4e3da', paperLo: '#d0d0c6', coverHi: '#2b3049', coverLo: '#10121e',
      gilt: '#8e9bb5', accent: '#3b4f7a', burn: '#2e3550', scuff: '#c9d2e6',
    },
  }),
  /** Tidewend harbour: sun-bleached, salt-stained paper with round worn corners and a driftwood cover. */
  harbour: defineVibe({
    label: 'Harbour',
    shape: { corners: 'round', roughness: 1.35, bow: 1.6 },
    texture: { kind: 'aged', stains: 2, burn: 1.25 },
    frame: [{ piece: 'vine', at: 'top-left' }, { piece: 'trail', at: 'bottom', along: 0.62 }, { piece: 'roots', at: 'bottom-left' }],
    palette: { paperHi: '#f7efdc', paperMid: '#ecdcbc', paperLo: '#dcc79f', coverHi: '#2b4644', coverLo: '#0f1f1e', accent: '#2f5c5a', scuff: '#e6d6ae' },
  }),
} satisfies Record<string, TomeVibe>;

export type TomeVibeName = keyof typeof TOME_VIBES;

export function isTomeVibeName(name: unknown): name is TomeVibeName {
  return typeof name === 'string' && Object.prototype.hasOwnProperty.call(TOME_VIBES, name);
}

/** A vibe by name (unknown names fall back to `default`) or as given, with optional small overrides. */
export function resolveVibe(vibe?: TomeVibeName | TomeVibe, overrides?: Omit<TomeVibeInput, 'label'>): TomeVibe {
  const base = typeof vibe === 'string' ? (isTomeVibeName(vibe) ? TOME_VIBES[vibe] : TOME_VIBES.default) : vibe ?? TOME_VIBES.default;
  return overrides ? defineVibe({ label: base.label, ...overrides }, base) : base;
}

const PALETTE_TOKENS: Record<keyof TomeVibePalette, string> = {
  paperHi: '--tome-paper-hi', paperMid: '--tome-paper-mid', paperLo: '--tome-paper-lo',
  coverHi: '--tome-cover-hi', coverLo: '--tome-cover-lo', gilt: '--tome-gilt', accent: '--tome-moss',
  burn: '--tome-edge-burn', scuff: '--tome-edge-scuff',
};

/**
 * The vibe as CSS custom properties: its palette plus `--tome-grain-amount`,
 * `--tome-burn` and `--tome-bow`. TomePanel puts them on its root; another
 * window can spread them into its own style to share the tome's tokens.
 */
export function tomeVibeStyle(vibe: TomeVibe): React.CSSProperties {
  const style: Record<string, string> = {
    '--tome-grain-amount': String(vibe.texture.grain),
    '--tome-burn': String(vibe.texture.burn),
    '--tome-bow': String(vibe.shape.bow),
  };
  for (const [key, token] of Object.entries(PALETTE_TOKENS) as Array<[keyof TomeVibePalette, string]>) {
    const value = vibe.palette[key];
    if (value) style[token] = value;
  }
  return style as React.CSSProperties;
}
